import { Zap } from 'lucide-react';

import {
  ModuleCards,
  SELECTION_LOCK_REASON,
  type EngagementModuleKey,
} from '@/components/engagement/module-cards';
import {
  type ModulesStepState,
  type NewEngagementWizardState,
} from '@/lib/wizard/new-engagement-state';

/**
 * Step 2 — Modules pane.
 *
 * Five toggleable cards (the shared `ModuleCards`, also used by the Edit
 * engagement details modal), matching the prototype's
 * `modals-wizard.js → stepModules()` + `wizard.css → .module-card`:
 *
 *   - Contract Compliance (E06) → contract_compliance
 *   - Health Check        (E07) → health_check
 *   - Go-Live Readiness   (E08) → go_live_readiness
 *   - Selection           (E11) → selection (T-0142; same card style)
 *   - Discovery           (E12) → discovery
 *
 * Discovery and Selection follow the Edit engagement details modal's rule,
 * which mirrors the API's (T-0171): while Discovery is ticked, Selection
 * shows ticked and locked with the reason on the card. The wizard sends
 * only what was ticked; the API turns Selection on with Discovery.
 *
 * The modules chosen here also decide how the wizard ends: an IV&V module
 * (Contract Compliance, Health Check, Go-Live Readiness) brings the
 * baseline documents and AI base formation steps; Discovery and/or
 * Selection alone bring one "Next steps" pane instead
 * (`wizardStepsFor` in the wizard state module).
 *
 * The DB CHECK enforces "at least one module enabled" at create time. The
 * wizard footer mirrors that by gating Continue on `isStepValid(modules)`.
 *
 * Visual contract: amber-tinted selected state per ADR-008 / R6 — token
 * `--accent-amber-600` for the active border, `--accent-amber-100` for
 * the icon tint when selected. Selected check pip in the top-right
 * matches the prototype.
 */
export interface StepProps {
  state: NewEngagementWizardState;
  setModules: (patch: Partial<ModulesStepState>) => void;
}

/** Wizard state key -> API module key, in the order the cards show. */
const WIZARD_MODULES: ReadonlyArray<[keyof ModulesStepState, EngagementModuleKey]> = [
  ['contract', 'contract_compliance'],
  ['health', 'health_check'],
  ['golive', 'go_live_readiness'],
  ['selection', 'selection'],
  ['discovery', 'discovery'],
];

export function StepModules({ state, setModules }: StepProps) {
  const { modules } = state;
  // Selection as shown is `selection || discovery`, as in the edit modal.
  const shown: Record<string, boolean> = Object.fromEntries(
    WIZARD_MODULES.map(([w, key]) => [key, Boolean(modules[w])]),
  );
  shown.selection = Boolean(modules.selection || modules.discovery);

  return (
    <div className="space-y-4">
      <p className="text-meta text-ink-500">
        Select the modules to enable for this engagement. At least one is required.
      </p>

      <ModuleCards
        modules={WIZARD_MODULES.map(([, key]) => key)}
        selected={shown}
        locked={modules.discovery ? { selection: SELECTION_LOCK_REASON } : undefined}
        onToggle={(key, next) => {
          const entry = WIZARD_MODULES.find(([, k]) => k === key);
          if (entry) setModules({ [entry[0]]: next } as Partial<ModulesStepState>);
        }}
      />

      <div className="rounded-card border border-ink-100 bg-paper-50 p-3.5">
        <div className="flex items-start gap-2.5">
          <Zap className="mt-0.5 h-3.5 w-3.5 flex-none text-ai-ink" aria-hidden="true" />
          <p className="text-meta leading-[18px] text-ink-500">
            Caliber will instantiate the registers, widgets, and AI orchestration policy for each
            enabled module. Health and readiness assessment defaults apply from the firm settings at
            creation; nothing waits on a methodology. Turning Discovery on also turns Selection on.
            Modules can be changed later from Details and modules on the engagement overview.
          </p>
        </div>
      </div>
    </div>
  );
}
